import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';

/**
 * Component that protects routes requiring authentication
 * Will redirect to login page if user is not authenticated
 * In development with BYPASS_AUTH=true, authentication is bypassed
 */
export default function ProtectedRoute() {
  const authContext = useAuth();
  const bypassAuth = import.meta.env.VITE_BYPASS_AUTH === 'true';
  
  // Handle case where auth context is undefined
  if (!authContext) {
    console.error('ProtectedRoute: AuthContext is undefined. Make sure AuthProvider wraps the app.');
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Typography variant="h6" color="error">
          Authentication Error: Context not available
        </Typography>
      </Box>
    );
  }
  
  const { isAuthenticated, loading } = authContext;
  
  // In development with bypass enabled, skip authentication checks
  if (bypassAuth) {
    console.log('Development mode: Authentication bypassed in ProtectedRoute');
    return <Outlet />;
  }

  // Show loading indicator while checking authentication status
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Checking authentication...
        </Typography>
      </Box>
    );
  }

  // Redirect to login page if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Render child routes if authenticated
  return <Outlet />;
}